import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Plus } from 'lucide-react'
import { MainLayout } from '@/components/layout/MainLayout'
import { Button } from '@/components/ui/button'
import { DashboardPage } from '@/components/dashboard/DashboardPrimitives'
import {
  ContentInsights,
  NeedsAttention,
  PerformanceSnapshot,
  StatTiles,
  TopAthletes,
} from '@/components/dashboard/home/HomeWidgets'
import { greeting } from '@/components/dashboard/home/format'
import { brandSetupItems, brandSetupScore } from '@/lib/brandProfile'
import { useGetMarketerDashboardQuery } from '@/redux/marketerApi'
import type { RootState } from '@/store'

/** A quiet reminder to finish brand setup, until the profile is complete */
const SetupReminder: React.FC<{ org: any }> = ({ org }) => {
  const score = brandSetupScore(org)
  if (score >= 100) return null
  const next = brandSetupItems(org).find((i) => !i.done)
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#C0FD72]/40 bg-[#C0FD72]/10 px-5 py-3">
      <p className="text-sm text-gray-700">
        <span className="font-semibold text-foreground">Your brand profile is {score}% done.</span>{' '}
        {next ? `Next: ${next.label.toLowerCase()}. ` : ''}A complete profile brings better
        matches.
      </p>
      <Button asChild variant="lime" size="sm">
        <Link to="/setup">Finish setup</Link>
      </Button>
    </div>
  )
}

/**
 * The brand's home. Top to bottom, in order of urgency: what needs them now (stats and the
 * punch list), who is performing, how content is doing, and which kinds of content to brief
 * more of.
 */
const Dashboard: React.FC = () => {
  const user = useSelector((state: RootState) => state.user.currentUser) as any
  const org = user?.org
  const firstName = (user?.name || user?.email?.split('@')[0] || 'there').split(' ')[0]
  const userName = firstName
  const [days, setDays] = useState(30)
  const { data, isLoading, isError } = useGetMarketerDashboardQuery(days, {
    refetchOnMountOrArgChange: true,
  })

  return (
    <MainLayout>
      <div className="mx-auto w-full max-w-[1400px] space-y-8 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        {/* Greeting and the one main action */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-base text-gray-500">{greeting()},</p>
            <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {firstName}
            </h1>
            <p className="mt-1 text-gray-500">Here's what's happening with your partnerships.</p>
          </div>
          <Button asChild variant="lime" className="h-11 rounded-xl px-5 text-base">
            <Link to="/campaign/create">
              <Plus className="h-5 w-5" />
              Create opportunity
            </Link>
          </Button>
        </div>

        <SetupReminder org={org} />

        {isLoading || !data ? (
          <p className="py-16 text-center text-sm text-gray-500">
            {isError ? "Couldn't load your dashboard. Refresh to try again." : 'Loading…'}
          </p>
        ) : (
          <>
            <StatTiles data={data} />

            {/* What needs them now, and who is doing well */}
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <NeedsAttention data={data} />
              <TopAthletes data={data} days={days} onDays={setDays} />
            </div>

            {/* How content is doing, and what to brief next */}
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <PerformanceSnapshot data={data} days={days} onDays={setDays} />
              <ContentInsights data={data} />
            </div>
          </>
        )}
      </div>
    </MainLayout>
  )
}

export default Dashboard
